import { ref } from "vue";
import { createAnchorForDownload, removeAnchor } from "@/services/DownloadFile";

/**
 * @composable useDownload
 * @description Gestisce download di file blob con tracking dello stato.
 *
 * Supporta due modalità:
 * - **Globale** (`id` omesso): `isDownloading` diventa true durante il download.
 *   Usato per export CSV o download singoli non tracciati per item.
 * - **Per item** (`id` fornito): `downloadingId` tiene l'id dell'item in download;
 *   gli altri item non risultano in caricamento. Usato per liste scaricabili.
 *
 * La funzione `fn` è un thunk che restituisce una Promise. Il risultato può essere:
 * - Risposta axios raw `{ data: Blob, headers }` (apiClient con responseType:'blob')
 * - Shape service `{ data: Blob, error }` (service con pattern { data, error })
 *
 * Se il server restituisce `content-disposition: attachment; filename="foo.csv"`,
 * il filename viene estratto automaticamente dall'header; altrimenti si usa il
 * fallback passato nell'opzione `filename`.
 *
 * @param {Object} [options]
 * @param {Function} [options.onError] - Callback eseguito in caso di errore
 * @returns {{
 *   isDownloading: import('vue').Ref<boolean>,
 *   downloadingId: import('vue').Ref<any>,
 *   download: Function
 * }}
 *
 * @example
 * // Export CSV (globale)
 * const { isDownloading, download } = useDownload()
 * await download(
 *   () => apiClient.get('tickets/v1/otrs', { params: { csv: 'true' }, headers: { Accept: 'text/csv' }, responseType: 'blob' }),
 *   { filename: 'report.csv' }
 * )
 *
 * @example
 * // Download per item (lista)
 * const { downloadingId, download } = useDownload({ onError: () => showError() })
 * await download(
 *   () => DocumentService.downloadDocument(doc.id, doc.category?.id),
 *   { id: doc.id, filename: doc.name }
 * )
 */
export function useDownload({ onError } = {}) {
  const isDownloading = ref(false);
  const downloadingId = ref(null);

  /**
   * Esegue il download.
   * @param {Function} fn - Thunk che restituisce la Promise del download
   * @param {Object} [opts]
   * @param {*}      [opts.id]       - Se fornito, attiva il tracking per item (downloadingId)
   * @param {string} [opts.filename] - Filename di fallback se il server non manda content-disposition
   * @returns {Promise<boolean>} true = successo, false = errore
   */
  async function download(fn, { id = null, filename = "download" } = {}) {
    const tracked = id !== null;
    if (tracked) downloadingId.value = id;
    else isDownloading.value = true;

    try {
      const result = await fn();

      if (result?.error) {
        console.error(result.error);
        onError?.(result.error);
        return false;
      }

      const blob = result?.data ?? result;

      if (!(blob instanceof Blob)) {
        const msg = "useDownload: il risultato non è un Blob";
        console.error(msg, result);
        onError?.(msg);
        return false;
      }

      // Parsing content-disposition (presente nelle risposte axios raw, non nelle shape service)
      const cd = result?.headers?.["content-disposition"];
      const resolvedFilename = cd
        ? (cd.match(/filename="(.+)"/)?.[1] ?? filename)
        : filename;

      const anchor = createAnchorForDownload(blob, false, resolvedFilename);
      anchor.click();
      removeAnchor(anchor);
      return true;
    } catch (err) {
      console.error(err);
      onError?.(err);
      return false;
    } finally {
      if (tracked) downloadingId.value = null;
      else isDownloading.value = false;
    }
  }

  return { isDownloading, downloadingId, download };
}
